import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Loader2, Trash2, Code, Eye, BarChart3, Copy, CheckCircle, Settings, X, Shield, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { getBanners, createBanner, deleteBanner, getBannerEmbedCode, getBannerAnalytics, getPurposes } from '@/services/dpdpa';

export default function ConsentBannerPage() {
  const [banners, setBanners] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEmbed, setShowEmbed] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(null);
  const [embedData, setEmbedData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [embedTab, setEmbedTab] = useState('js');
  const [showPreferences, setShowPreferences] = useState(null); // {banner, purposes} or 'create' for form preview
  const [form, setForm] = useState({
    name: '', domain: '', position: 'bottom', layout: 'bar',
    primary_color: '#1e3a5f', text_color: '#ffffff', bg_color: '#1e3a5f',
    heading_text: 'We value your privacy',
    body_text: 'We use cookies and process personal data as per DPDPA 2023. Please choose your preferences.',
    accept_text: 'Accept All', reject_text: 'Reject All', customize_text: 'Customize',
    purpose_ids: [], show_purposes: true, active: true,
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [bRes, pRes] = await Promise.all([getBanners(), getPurposes()]);
      setBanners(bRes.banners || []);
      setPurposes((pRes.purposes || []).filter(p => p.status === 'active'));
    } catch (err) {
      toast.error('Failed to load banners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.name) { toast.error('Banner name is required'); return; }
    try {
      setSaving(true);
      await createBanner(form);
      toast.success('Consent banner created');
      setShowCreate(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to create banner');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await deleteBanner(id);
      toast.success('Banner deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleGetEmbed = async (banner) => {
    try {
      setShowEmbed(banner);
      const data = await getBannerEmbedCode(banner.id);
      setEmbedData(data);
    } catch (err) {
      toast.error('Failed to get embed code');
    }
  };

  const handleGetAnalytics = async (banner) => {
    try {
      setShowAnalytics(banner);
      const data = await getBannerAnalytics(banner.id);
      setAnalyticsData(data);
    } catch (err) {
      toast.error('Failed to load analytics');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const togglePurpose = (pid) => {
    setForm(prev => ({
      ...prev,
      purpose_ids: prev.purpose_ids.includes(pid) ? prev.purpose_ids.filter(p => p !== pid) : [...prev.purpose_ids, pid]
    }));
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="consent-banner-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Consent Banner Builder</h1>
          <p className="text-sm opacity-60">Create embeddable DPDPA consent banners for your websites and apps</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-banner-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Banner
        </Button>
      </div>

      {banners.length === 0 ? (
        <Card><CardContent className="p-8 text-center opacity-50"><Code className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No consent banners created yet. Create one to embed on your websites.</p></CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {banners.map((b) => (
            <Card key={b.id} data-testid={`banner-card-${b.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold">{b.name}</h3>
                      <Badge variant={b.active ? 'default' : 'secondary'}>{b.active ? 'Active' : 'Inactive'}</Badge>
                      {b.domain && <Badge variant="outline" className="text-xs">{b.domain}</Badge>}
                      <Badge variant="outline" className="text-xs capitalize">{b.position} / {b.layout}</Badge>
                    </div>
                    {/* Live Preview */}
                    <div className="rounded-lg border p-3 mt-2" style={{ background: b.bg_color, color: b.text_color }}>
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div style={{ flex: 1, minWidth: 200 }}>
                          <strong className="text-sm">{b.heading_text}</strong>
                          <p className="text-xs mt-1 opacity-85">{b.body_text}</p>
                          <p className="text-[10px] mt-1 opacity-50">Non-essential cookies are off by default</p>
                        </div>
                        <div className="flex gap-2 items-center flex-wrap">
                          <span className="px-3 py-1.5 rounded text-xs font-semibold" style={{ background: b.primary_color, color: 'white' }}>{b.accept_text}</span>
                          <button
                            type="button"
                            onClick={() => setShowPreferences({ banner: b, purposes: purposes.filter(p => (b.purpose_ids || []).includes(p.id)) })}
                            className="px-3 py-1.5 rounded text-xs cursor-pointer hover:opacity-80 transition-opacity"
                            style={{ border: `1px solid ${b.text_color}`, color: b.text_color, background: 'transparent' }}
                            data-testid={`preview-manage-${b.id}`}
                          >
                            {b.customize_text || 'Customize'}
                          </button>
                          <span className="px-3 py-1.5 rounded text-xs border" style={{ borderColor: b.text_color, color: b.text_color }}>{b.reject_text}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-2 text-xs opacity-50">
                      <span>Impressions: {b.impressions || 0}</span>
                      <span>Accepts: {b.accepts || 0}</span>
                      <span>Rejects: {b.rejects || 0}</span>
                      <span>Purposes: {(b.purpose_ids || []).length}</span>
                    </div>
                  </div>
                  <div className="flex gap-1 ml-3">
                    <Button variant="outline" size="sm" onClick={() => handleGetEmbed(b)} data-testid={`embed-banner-${b.id}`}>
                      <Code className="w-3 h-3 mr-1" /> Embed
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleGetAnalytics(b)} data-testid={`analytics-banner-${b.id}`}>
                      <BarChart3 className="w-3 h-3 mr-1" /> Stats
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(b.id)}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Banner Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-banner-dialog">
          <DialogHeader><DialogTitle>Create Consent Banner</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Banner Name</Label><Input value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. Main Website Banner" data-testid="banner-name-input" /></div>
              <div><Label>Domain (optional)</Label><Input value={form.domain} onChange={e => setForm(prev => ({ ...prev, domain: e.target.value }))} placeholder="e.g. example.com" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Position</Label>
                <Select value={form.position} onValueChange={v => setForm(prev => ({ ...prev, position: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="bottom">Bottom</SelectItem><SelectItem value="top">Top</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Layout</Label>
                <Select value={form.layout} onValueChange={v => setForm(prev => ({ ...prev, layout: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="bar">Bar</SelectItem><SelectItem value="box">Box</SelectItem><SelectItem value="popup">Popup</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2"><Switch checked={form.active} onCheckedChange={v => setForm(prev => ({ ...prev, active: v }))} /><Label>Active</Label></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Background</Label><Input type="color" value={form.bg_color} onChange={e => setForm(prev => ({ ...prev, bg_color: e.target.value }))} className="h-10" /></div>
              <div><Label>Text Color</Label><Input type="color" value={form.text_color} onChange={e => setForm(prev => ({ ...prev, text_color: e.target.value }))} className="h-10" /></div>
              <div><Label>Primary Color</Label><Input type="color" value={form.primary_color} onChange={e => setForm(prev => ({ ...prev, primary_color: e.target.value }))} className="h-10" /></div>
            </div>
            <div><Label>Heading</Label><Input value={form.heading_text} onChange={e => setForm(prev => ({ ...prev, heading_text: e.target.value }))} /></div>
            <div><Label>Body Text</Label><Textarea value={form.body_text} onChange={e => setForm(prev => ({ ...prev, body_text: e.target.value }))} rows={2} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Accept Button</Label><Input value={form.accept_text} onChange={e => setForm(prev => ({ ...prev, accept_text: e.target.value }))} /></div>
              <div><Label>Reject Button</Label><Input value={form.reject_text} onChange={e => setForm(prev => ({ ...prev, reject_text: e.target.value }))} /></div>
              <div><Label>Customize Button</Label><Input value={form.customize_text} onChange={e => setForm(prev => ({ ...prev, customize_text: e.target.value }))} /></div>
            </div>
            <div>
              <Label>Consent Purposes</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {purposes.map(p => (
                  <Badge key={p.id} variant={form.purpose_ids.includes(p.id) ? 'default' : 'outline'} className="cursor-pointer text-xs" onClick={() => togglePurpose(p.id)}>
                    {p.name} {p.is_mandatory && '(Required)'}
                  </Badge>
                ))}
              </div>
            </div>
            {/* Live Preview */}
            <div>
              <Label>Preview</Label>
              <div className="rounded-lg border p-3 mt-1" style={{ background: form.bg_color, color: form.text_color }}>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <strong className="text-sm">{form.heading_text}</strong>
                    <p className="text-xs mt-1 opacity-85">{form.body_text}</p>
                    <p className="text-[10px] mt-1 opacity-50">Non-essential cookies are off by default</p>
                  </div>
                  <div className="flex gap-2 items-center flex-wrap">
                    <span className="px-3 py-1.5 rounded text-xs font-semibold" style={{ background: form.primary_color, color: 'white' }}>{form.accept_text}</span>
                    <button
                      type="button"
                      onClick={() => setShowPreferences('create')}
                      className="px-3 py-1.5 rounded text-xs cursor-pointer hover:opacity-80 transition-opacity"
                      style={{ border: `1px solid ${form.text_color}`, color: form.text_color, background: 'transparent' }}
                      data-testid="preview-customize-btn"
                    >
                      {form.customize_text || 'Customize'}
                    </button>
                    <span className="px-3 py-1.5 rounded text-xs border" style={{ borderColor: form.text_color, color: form.text_color }}>{form.reject_text}</span>
                  </div>
                </div>
              </div>
              {form.purpose_ids.length > 0 && (
                <p className="text-[10px] mt-1 opacity-40">Click "{form.customize_text || 'Customize'}" to preview the Manage Preferences modal</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="save-banner-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Create Banner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Embed Code Dialog */}
      <Dialog open={!!showEmbed} onOpenChange={(v) => { if (!v) { setShowEmbed(null); setEmbedData(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="embed-code-dialog">
          <DialogHeader><DialogTitle>Embed Code — {showEmbed?.name}</DialogTitle></DialogHeader>
          {embedData ? (
            <Tabs value={embedTab} onValueChange={setEmbedTab}>
              <TabsList><TabsTrigger value="js">JS Snippet</TabsTrigger><TabsTrigger value="react">React Component</TabsTrigger></TabsList>
              <TabsContent value="js" className="mt-3">
                <p className="text-xs opacity-60 mb-2">Copy and paste this script tag into your website's HTML, just before the closing &lt;/body&gt; tag.</p>
                <div className="relative">
                  <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto max-h-64">{embedData.js_snippet}</pre>
                  <Button variant="ghost" size="sm" className="absolute top-2 right-2" onClick={() => copyToClipboard(embedData.js_snippet)} data-testid="copy-js-snippet"><Copy className="w-3 h-3" /></Button>
                </div>
              </TabsContent>
              <TabsContent value="react" className="mt-3">
                <p className="text-xs opacity-60 mb-2">Use this React component in your React application. Import and render it in your root component.</p>
                <div className="relative">
                  <pre className="bg-gray-900 text-blue-400 p-4 rounded-lg text-xs overflow-x-auto max-h-64">{embedData.react_component}</pre>
                  <Button variant="ghost" size="sm" className="absolute top-2 right-2" onClick={() => copyToClipboard(embedData.react_component)} data-testid="copy-react-snippet"><Copy className="w-3 h-3" /></Button>
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          )}
        </DialogContent>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={!!showAnalytics} onOpenChange={(v) => { if (!v) { setShowAnalytics(null); setAnalyticsData(null); } }}>
        <DialogContent data-testid="banner-analytics-dialog">
          <DialogHeader><DialogTitle>Banner Analytics — {showAnalytics?.name}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Analytics for this consent banner</DialogDescription>
          {analyticsData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold">{analyticsData.impressions}</p><p className="text-xs opacity-60">Impressions</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-emerald-500">{analyticsData.accepts}</p><p className="text-xs opacity-60">Accepts</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-red-500">{analyticsData.rejects}</p><p className="text-xs opacity-60">Rejects</p></CardContent></Card>
                <Card><CardContent className="p-3 text-center"><p className="text-xl font-bold text-blue-500">{analyticsData.accept_rate}%</p><p className="text-xs opacity-60">Accept Rate</p></CardContent></Card>
              </div>
              {analyticsData.recent_consents?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Recent Consents</h4>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {analyticsData.recent_consents.map((c, i) => (
                      <div key={i} className="flex items-center justify-between text-xs p-2 bg-black/5 rounded">
                        <span>{c.principal_identifier}</span>
                        <span className="opacity-50">{new Date(c.created_at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manage Cookie Preferences Preview Dialog */}
      <Dialog open={!!showPreferences} onOpenChange={(v) => { if (!v) setShowPreferences(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="manage-preferences-preview">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-4 h-4" /> Manage Cookie Preferences
            </DialogTitle>
            <DialogDescription className="text-xs">
              Preview of what end-users will see. Non-essential cookies are OFF by default.
            </DialogDescription>
          </DialogHeader>
          {showPreferences && (() => {
            const previewPurposes = showPreferences === 'create'
              ? purposes.filter(p => form.purpose_ids.includes(p.id))
              : (showPreferences.purposes || []);
            const previewColor = showPreferences === 'create' ? form.primary_color : showPreferences.banner?.primary_color;

            return (
              <div className="space-y-1" data-testid="preferences-purpose-list">
                {previewPurposes.length === 0 ? (
                  <div className="text-center py-8 opacity-50">
                    <Shield className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No consent purposes selected.</p>
                    <p className="text-xs mt-1">Select purposes in the banner form to preview them here.</p>
                  </div>
                ) : (
                  previewPurposes.map((p) => (
                    <div key={p.id} className="flex items-center justify-between py-3 border-b last:border-0" data-testid={`pref-purpose-${p.code || p.id}`}>
                      <div className="flex-1 min-w-0 mr-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium">{p.name}</span>
                          {p.is_mandatory && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Required</Badge>
                          )}
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize">{p.category}</Badge>
                        </div>
                        {p.description && (
                          <p className="text-xs opacity-50 mt-0.5 truncate">{p.description}</p>
                        )}
                        <div className="flex items-center gap-1 mt-1 text-xs opacity-60">
                          <Clock className="w-3 h-3" />
                          <span>Retention: <strong>{p.retention_period_days || 365} days</strong></span>
                        </div>
                      </div>
                      <Switch
                        checked={p.is_mandatory}
                        disabled
                        className="shrink-0"
                        data-testid={`pref-toggle-${p.code || p.id}`}
                      />
                    </div>
                  ))
                )}

                {/* Footer buttons preview */}
                <div className="flex items-center justify-between pt-4 gap-2 flex-wrap">
                  <Button variant="outline" size="sm" className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600" disabled data-testid="pref-withdraw-btn">
                    Withdraw Consent
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled>Cancel</Button>
                    <Button size="sm" disabled style={{ background: previewColor, borderColor: previewColor }} data-testid="pref-save-btn">
                      Save Preferences
                    </Button>
                  </div>
                </div>

                {/* Info box */}
                <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-3 mt-2">
                  <p className="text-xs font-medium text-blue-700 dark:text-blue-300">How it works for end-users:</p>
                  <ul className="text-[11px] text-blue-600 dark:text-blue-400 mt-1 space-y-0.5 list-disc list-inside">
                    <li>Required purposes are always ON and cannot be toggled off</li>
                    <li>Non-essential purposes (analytics, marketing, etc.) are OFF by default</li>
                    <li>Users can toggle individual purposes and save preferences</li>
                    <li>A persistent "Manage Preferences" button stays on-screen for easy withdrawal</li>
                    <li>Each purpose shows its data retention period</li>
                    <li>Available in all 22 Eighth Schedule languages + English</li>
                  </ul>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
